import { z } from 'zod';
import { normalizeText } from './importer';

/**
 * Mapeo de columnas guardado por cuenta (Kilo, oct 2026).
 *
 * Una operacion madura no cambia de archivo: cada mes exporta el mismo
 * registro de ventas del TPV o el mismo libro de compras del ERP. Rehacer el
 * emparejado de columnas en cada carga era el «te lo adaptamos a tu Excel»
 * convertido en trabajo fijo. Ahora la primera importacion que sale bien
 * guarda que es cada columna y la siguiente con las mismas cabeceras llega
 * ya emparejada: subir, revisar la vista previa, importar.
 *
 * Se guarda por NOMBRE de cabecera normalizado, no por posicion: si el TPV
 * anyade o mueve una columna, las conocidas siguen encajando y la nueva se
 * adivina como siempre. El valor es la clave del destino (`targetKey` en
 * ventas, el destino tal cual en gastos), asi este modulo no sabe nada de
 * los destinos de cada importador.
 */

export const IMPORT_MAPPING_KINDS = ['ventas', 'unidades', 'gastos'] as const;
export type ImportMappingKind = (typeof IMPORT_MAPPING_KINDS)[number];

export interface SavedImportMapping {
  /** Cabeceras normalizadas y ordenadas: identifica el formato del archivo. */
  signature: string;
  /** Cabeceras tal y como venian, para ensenarlas. */
  headers: string[];
  /** Cabecera normalizada → clave del destino. Solo las que se importan. */
  columns: Record<string, string>;
  /** Ventas: la respuesta a «¿los importes llevan IVA?» de la ultima vez. */
  amountsIncludeVat?: boolean;
  /** Nombre del ultimo archivo importado con este formato. */
  fileName?: string;
  usedAt: string;
}

export interface ImportMappingInput {
  headers: string[];
  columns: Record<string, string>;
  amountsIncludeVat?: boolean;
  fileName?: string;
}

/** Formatos distintos que se recuerdan por tipo: el mas antiguo se olvida. */
export const MAX_SAVED_MAPPINGS = 10;

export const importMappingInputSchema = z.object({
  headers: z.array(z.string().max(200)).min(1).max(200),
  columns: z.record(z.string().max(200), z.string().min(1).max(80)),
  amountsIncludeVat: z.boolean().optional(),
  fileName: z.string().max(200).optional(),
});

/** Cabeceras utiles, normalizadas: sin las vacias, que no identifican nada. */
function normalizadas(headers: string[]): string[] {
  return headers.map(normalizeText).filter((h) => h !== '');
}

export function headerSignature(headers: string[]): string {
  return [...new Set(normalizadas(headers))].sort().join('|');
}

/**
 * El mapeo guardado que mejor encaja con estas cabeceras, ya traducido a una
 * clave por columna (`undefined` = esa columna no estaba en el guardado y se
 * adivina como siempre).
 *
 * Encaja si coincide la firma, o si al menos 2 columnas y el 60 % de las del
 * archivo ya se conocian: lo bastante para no aplicar el mapeo de otro
 * informe que comparte por casualidad «Fecha» y «Total».
 */
export function pickSavedMapping(
  saved: SavedImportMapping[],
  headers: string[],
): { mapping: SavedImportMapping; keys: Array<string | undefined>; exact: boolean } | null {
  const propias = normalizadas(headers);
  if (propias.length === 0) return null;
  const firma = headerSignature(headers);
  const minimo = Math.max(2, Math.ceil(propias.length * 0.6));

  let mejor: { mapping: SavedImportMapping; conocidas: number } | null = null;
  for (const mapping of saved) {
    const conocidas = new Set(normalizadas(mapping.headers));
    const comunes = propias.filter((h) => conocidas.has(h)).length;
    const exacta = mapping.signature === firma;
    if (!exacta && comunes < minimo) continue;
    // La firma exacta gana siempre; entre parciales, la que mas conoce y,
    // a igualdad, la mas reciente (la lista llega ordenada por uso).
    const puntos = exacta ? Number.MAX_SAFE_INTEGER : comunes;
    if (!mejor || puntos > mejor.conocidas) mejor = { mapping, conocidas: puntos };
  }
  if (!mejor) return null;

  const { mapping } = mejor;
  const conocidas = new Set(normalizadas(mapping.headers));
  const keys = headers.map((h) => {
    const n = normalizeText(h);
    if (!n || !conocidas.has(n)) return undefined;
    // Conocida y sin destino guardado = la ultima vez se dejo sin importar.
    return mapping.columns[n] ?? 'ignore';
  });
  return { mapping, keys, exact: mapping.signature === firma };
}

/**
 * Anyade o actualiza el formato y lo pone el primero. Las columnas «ignore»
 * no se guardan: basta con que la cabecera este en `headers`.
 */
export function rememberMapping(
  saved: SavedImportMapping[],
  input: ImportMappingInput,
  now: string,
): SavedImportMapping[] {
  const columns: Record<string, string> = {};
  for (const [header, key] of Object.entries(input.columns)) {
    const n = normalizeText(header);
    if (n && key !== 'ignore') columns[n] = key;
  }
  const nuevo: SavedImportMapping = {
    signature: headerSignature(input.headers),
    headers: input.headers,
    columns,
    ...(input.amountsIncludeVat !== undefined ? { amountsIncludeVat: input.amountsIncludeVat } : {}),
    ...(input.fileName ? { fileName: input.fileName } : {}),
    usedAt: now,
  };
  return [nuevo, ...saved.filter((m) => m.signature !== nuevo.signature)].slice(0, MAX_SAVED_MAPPINGS);
}
